// The streamed valley's tours (phase C joined): gate C3's six place-and-time shots taken again in the real valley (the
// sky stream took them in its stand-in lab), and gate C6's route tour: @rpg/perf's tour driving the route bot over the
// whole route on a tier, with the sky running (the clock fast enough that the time of day changes along the route,
// rain over the town). Both read the counts per segment (draw calls, triangles), the post passes and the shadow map,
// and the shader compiles after ready (the harness's watchdog). Framings and hours are the integrator's (agent).
import type * as THREE from "three";
import { overlay, perfReport, runTour, type TourSegment } from "@rpg/perf";
import type { LowPolyLook } from "@rpg/look";
import type { Runtime } from "@rpg/runtime";
import { SKY_ACTIONS } from "../sky/module";
import type { WorldMount } from "./scene";
import { routeLegs } from "./bots";
import { teleportHero } from "./teleport";

/** One shot in the valley: its hour, weather and wetness, where the hero stands and faces (the follow camera frames it
 *  from behind), or a house to walk into. */
export interface WorldShot {
  hours: number;
  weather: string;
  wet?: number;
  /** [x, z, yaw] of the hero, or the room to walk into. */
  at: [number, number, number] | { room: string };
}

/** The six place-and-time pairs (gate C3, the brief's: meadow at dawn, forest at noon, town at dusk, ruins at night, the
 *  town in rain, an interior), placed in the layout. */
export const WORLD_SHOTS: Record<string, WorldShot> = {
  "meadow-dawn": { hours: 6.15, weather: "clear", at: [-138, 141, 1.25] },
  "forest-noon": { hours: 12, weather: "clear", at: [-21.6, 26, 2.32] },
  "town-dusk": { hours: 18.15, weather: "clear", at: [70, -19, Math.PI] },
  "ruins-night": { hours: 23, weather: "clear", at: [129, -110, Math.PI] },
  "town-rain": { hours: 14, weather: "rain", wet: 1, at: [57, -33, Math.PI / 2] },
  interior: { hours: 20.5, weather: "clear", at: { room: "house-1" } },
};
export type WorldShotName = keyof typeof WORLD_SHOTS;

/** The C6 route's legs by region: the tour's segments, and what the sky does at each (rain over the town). */
const ROUTE_REGIONS = ["meadow", "forest", "town", "ruins"] as const;

export function worldTours(o: {
  m: WorldMount;
  runtime: Runtime;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  look: () => LowPolyLook | null;
  tier: "low" | "high";
  dispatch: (action: string, payload: unknown) => void;
  approach: (room: string, dist?: number) => unknown;
  bot: { start(o?: { frameMs?: number; draw?: boolean; tilt?: number }): void; stop(): void; legsDone(): number };
}) {
  const { m, runtime } = o;
  const rig = () => m.rig();
  const place = (x: number, z: number, yaw: number): void => {
    const r = rig();
    teleportHero(m, x, z, yaw);
    r.yawHeld = true;
    r.camera?.setYaw(yaw);
    r.control().cameraYaw = yaw;
  };
  /** Set a shot's hour and weather in the sim and put the hero there (no time passes). */
  const setShot = (name: string): void => {
    const s = WORLD_SHOTS[name];
    if (!s) throw new Error(`world: no shot "${name}" (have ${Object.keys(WORLD_SHOTS).join(", ")})`);
    o.dispatch(SKY_ACTIONS.set, { hours: s.hours });
    o.dispatch(SKY_ACTIONS.weather, { id: s.weather, instant: true });
    o.dispatch(SKY_ACTIONS.wet, { wet: s.wet ?? 0 });
    rig().actions.setStick("move", null);
    if (Array.isArray(s.at)) place(...s.at);
    else o.approach(s.at.room, 2.6);
  };
  /** Frame a shot: set it, then play `ms` of game time in 50 ms drawn frames (the camera settles, the chunks stream,
   *  the rain falls); into a room, the hero walks in through the door first. */
  const shot = (name: string, ms = 1200): void => {
    setShot(name);
    const s = WORLD_SHOTS[name]!;
    if (!Array.isArray(s.at)) {
      rig().actions.setStick("move", { x: 0, y: 1 });
      for (let t = 0; t < 1000; t += 50) runtime.advance(50, { draw: true });
      rig().actions.setStick("move", { x: 0, y: 0 });
    }
    for (let t = 0; t < ms; t += 50) runtime.advance(50, { draw: true });
  };
  const tour = (kind: string, route: string, segs: TourSegment[], ffMs: number, est: number, extra: () => Record<string, unknown>, onEnd?: () => void): void => {
    const look = o.look();
    if (!look) throw new Error("world tour: no look");
    const ui = overlay(o.tier);
    document.getElementById("perf-tour")?.setAttribute("data-no-strings", "");
    const t0 = { s: 0 };
    for (const sg of segs) {
      const st = sg.start;
      sg.start = () => {
        t0.s = runtime.clock.time;
        st?.();
      };
      const tick = sg.tick;
      sg.tick = (t) => tick(t - t0.s);
    }
    runTour({
      kind,
      renderer: o.renderer,
      scene: o.scene,
      composer: look.composer,
      game: { simT: () => runtime.clock.time, pause: (on) => void (runtime.paused = on), advanceTime: (ms, draw) => void runtime.advance(ms, { draw: draw ?? true }) },
      segments: segs,
      ffMs,
      route,
      estSeconds: est,
      tier: o.tier,
      ui,
      readyAt: performance.now(),
      quality: () => ({ ...look.report() }),
      extra,
      ...(onEnd ? { onEnd } : {}),
    });
  };
  return {
    shots: () => Object.keys(WORLD_SHOTS),
    shot,
    setShot,
    /** Gate C3 in the valley: the perf tour over the six shots, each held 1.2 s of game time. */
    lookTour(ffMs = 100): void {
      const segs: TourSegment[] = [{ name: "warmup", start: () => setShot("meadow-dawn"), tick: (t) => t > 0.6, max: 4 }];
      for (const name of Object.keys(WORLD_SHOTS)) {
        const walkIn = !Array.isArray(WORLD_SHOTS[name]!.at);
        segs.push({
          name,
          start: () => {
            setShot(name);
            if (walkIn) rig().actions.setStick("move", { x: 0, y: 1 });
          },
          tick: (t) => {
            if (walkIn && t > 1) rig().actions.setStick("move", { x: 0, y: 0 });
            return t > (walkIn ? 2.2 : 1.2);
          },
          max: 6,
        });
      }
      tour("world-look", "valley-shots", segs, ffMs, 12, () => ({ sky: m.sky().state() }), () => {
        rig().actions.setStick("move", null);
        rig().yawHeld = false;
      });
    },
    /** Gate C6: the route bot over the whole route on this tier, the sky running: from 6 h at `rate` (5: about 17 game
     *  hours over the walk, so the light goes from dawn to night), rain over the town, clear again at the ruins. One
     *  segment per region the route crosses. */
    routeTour(ffMs = 250, rate = 5): void {
      const { legs } = routeLegs(m);
      // the leg index where the route enters each region (the legs' end points' regions)
      const enter: number[] = ROUTE_REGIONS.map((r) => legs.findIndex((l) => m.world.terrain.regionAt(l.to.x, l.to.z) === r || (r === "town" && m.world.terrain.regionAt(l.to.x, l.to.z) === "square")));
      const ends = ROUTE_REGIONS.map((_, i) => (i + 1 < enter.length ? Math.max(1, enter[i + 1]!) : legs.length));
      const segs: TourSegment[] = [
        {
          name: "warmup",
          start: () => {
            o.dispatch(SKY_ACTIONS.set, { hours: 6 });
            o.dispatch(SKY_ACTIONS.weather, { id: "clear", instant: true });
            o.dispatch(SKY_ACTIONS.wet, { wet: 0 });
            o.dispatch(SKY_ACTIONS.rate, { rate });
            o.bot.start({ frameMs: ffMs, draw: true });
          },
          tick: (t) => t > 0.5,
          max: 4,
        },
      ];
      ROUTE_REGIONS.forEach((r, i) => {
        segs.push({
          name: r,
          start: () => {
            if (r === "town") o.dispatch(SKY_ACTIONS.weather, { id: "rain" });
            if (r === "ruins") o.dispatch(SKY_ACTIONS.weather, { id: "clear" });
          },
          tick: () => o.bot.legsDone() >= ends[i]!,
          max: 200,
        });
      });
      tour("world-route", "valley-route", segs, ffMs, 200, () => ({ sky: m.sky().state(), legs: { done: o.bot.legsDone(), total: legs.length, enter, ends } }), () => {
        o.bot.stop();
        o.dispatch(SKY_ACTIONS.rate, { rate: 1 });
      });
    },
    perf: () => perfReport(),
  };
}
